// AICA StatusBadge Component
// Badge for displaying record pipeline status
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  CheckCircle2,
  XCircle,
  Radio,
  FileAudio,
  Mic,
  Brain,
  Send,
  type LucideIcon,
} from 'lucide-react';
import type { RecordState } from '@aica/core';

// Re-export RecordState for convenience
export type { RecordState };

// Constants
const MAX_STATUS_LENGTH = 50;
const SPINNING_STATUSES = new Set<RecordState>([
  'transcribing',
  'analyzing',
  'delivering',
]);

// Type definitions
type StatusConfig = {
  labelKey: string;
  icon: LucideIcon;
  classNames: string[];
};

const statusConfig: Record<RecordState, StatusConfig> = {
  event_received: {
    labelKey: 'records.processingStatus.event_received',
    icon: Radio,
    classNames: ['status-badge', 'status-badge-neutral'],
  },
  pending_media: {
    labelKey: 'records.processingStatus.pending_media',
    icon: FileAudio,
    classNames: ['status-badge', 'status-badge-warning'],
  },
  media_detected: {
    labelKey: 'records.processingStatus.media_detected',
    icon: CheckCircle2,
    classNames: ['status-badge', 'status-badge-info'],
  },
  transcribing: {
    labelKey: 'records.processingStatus.transcribing',
    icon: Mic,
    classNames: ['status-badge', 'status-badge-info', 'animate-pulse'],
  },
  transcribed: {
    labelKey: 'records.processingStatus.transcribed',
    icon: CheckCircle2,
    classNames: ['status-badge', 'status-badge-info'],
  },
  analyzing: {
    labelKey: 'records.processingStatus.analyzing',
    icon: Brain,
    classNames: ['status-badge', 'status-badge-info', 'animate-pulse'],
  },
  analyzed: {
    labelKey: 'records.processingStatus.analyzed',
    icon: CheckCircle2,
    classNames: ['status-badge', 'status-badge-info'],
  },
  delivering: {
    labelKey: 'records.processingStatus.delivering',
    icon: Send,
    classNames: ['status-badge', 'status-badge-info', 'animate-pulse'],
  },
  completed: {
    labelKey: 'records.processingStatus.completed',
    icon: CheckCircle2,
    classNames: ['status-badge', 'status-badge-success'],
  },
  failed: {
    labelKey: 'records.processingStatus.failed',
    icon: XCircle,
    classNames: ['status-badge', 'status-badge-danger'],
  },
};

export interface StatusBadgeProps {
  status: RecordState | string;
  label?: string;
  showIcon?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  'data-testid'?: string; // Allow custom test ID
}

// Extended status configs for specific failure types and additional statuses
const extendedStatusConfig: Record<string, StatusConfig> = {
  pending: {
    labelKey: 'records.processingStatus.pending',
    icon: Radio,
    classNames: ['status-badge', 'status-badge-warning'],
  },
  // Specific failure types
  analysis_failed: {
    labelKey: 'records.processingStatus.analysis_failed',
    icon: XCircle,
    classNames: ['status-badge', 'status-badge-danger'],
  },
  delivery_failed: {
    labelKey: 'records.processingStatus.delivery_failed',
    icon: XCircle,
    classNames: ['status-badge', 'status-badge-danger'],
  },
  transcription_failed: {
    labelKey: 'records.processingStatus.transcription_failed',
    icon: XCircle,
    classNames: ['status-badge', 'status-badge-danger'],
  },
  media_failed: {
    labelKey: 'records.processingStatus.media_failed',
    icon: XCircle,
    classNames: ['status-badge', 'status-badge-warning'],
  },
  // API statuses from recordStatusSchema not in core RecordState
  awaiting_media: {
    labelKey: 'records.processingStatus.awaiting_media',
    icon: FileAudio,
    classNames: ['status-badge', 'status-badge-warning'],
  },
  awaiting_chat_completion: {
    labelKey: 'records.processingStatus.awaiting_chat_completion',
    icon: Radio,
    classNames: ['status-badge', 'status-badge-warning'],
  },
  delivered: {
    labelKey: 'records.processingStatus.delivered',
    icon: Send,
    classNames: ['status-badge', 'status-badge-success'],
  },
  not_applicable: {
    labelKey: 'records.processingStatus.not_applicable',
    icon: XCircle,
    classNames: ['status-badge', 'status-badge-neutral'],
  },
};

// Fallback config for unknown statuses
const fallbackConfig: StatusConfig = {
  labelKey: 'records.processingStatus.unknown',
  icon: Radio,
  classNames: ['status-badge', 'status-badge-neutral'],
};

/**
 * Sanitizes and validates status input
 * @param status - Raw status value from API or props
 * @returns Sanitized status string or 'unknown' if invalid
 */
function sanitizeStatus(status: unknown): string {
  if (status === null || status === undefined) {
    return 'unknown';
  }

  if (typeof status !== 'string') {
    return 'unknown';
  }

  // Trim and check for empty string
  const trimmed = status.trim();
  if (trimmed === '') {
    return 'unknown';
  }

  // Prevent DoS attacks with extremely long strings
  if (trimmed.length > MAX_STATUS_LENGTH) {
    return 'unknown';
  }

  return trimmed;
}

/**
 * Gets status configuration, falling back to default if status is unknown
 * @param status - Status value (RecordState or extended status string)
 * @returns Status configuration object
 */
function getStatusConfig(status: string): StatusConfig {
  // First check core RecordState config
  const coreConfig = statusConfig[status as RecordState];
  if (coreConfig) {
    return coreConfig;
  }
  // Then check extended status configs (specific failure types like analysis_failed)
  return extendedStatusConfig[status] || fallbackConfig;
}

/**
 * Renders the badge JSX structure
 * Extracted to reduce duplication between fallback and normal cases
 */
function renderBadge({
  config,
  label,
  showIcon,
  size,
  className,
  testId = 'status-badge',
  isSpinning = false,
}: {
  config: StatusConfig;
  label: string; // Already translated
  showIcon: boolean;
  size: 'sm' | 'md';
  className?: string;
  testId?: string;
  isSpinning?: boolean;
}) {
  const Icon = config.icon;
  const displayLabel = label;
  const iconClassName = cn(
    size === 'sm' ? 'h-3 w-3' : 'h-4 w-4',
    isSpinning && 'animate-spin'
  );

  return (
    <span
      data-testid={testId}
      className={cn(
        // Bloomberg Terminal styling - base classes applied via config.className
        'inline-flex items-center gap-1.5',
        // Size-specific overrides (status-badge already has padding)
        size === 'sm' && 'text-micro',
        size === 'md' && 'text-xs',
        ...config.classNames,
        className
      )}
    >
      {showIcon && <Icon data-testid="status-badge-icon" className={iconClassName} />}
      <span data-testid="status-badge-label">{displayLabel}</span>
    </span>
  );
}

export const StatusBadge = memo(function StatusBadge({
  status,
  label,
  showIcon = true,
  size = 'sm',
  className,
  'data-testid': testId,
}: StatusBadgeProps) {
  const { t } = useTranslation(['records', 'common']);

  // Sanitize and validate status input
  const sanitizedStatus = useMemo(() => sanitizeStatus(status), [status]);

  // Get status configuration
  const config = useMemo(() => getStatusConfig(sanitizedStatus), [sanitizedStatus]);

  // Check if status should show spinner animation
  const isSpinningStatus = useMemo(
    () => SPINNING_STATUSES.has(sanitizedStatus as RecordState),
    [sanitizedStatus]
  );

  // Get translated label - use translation key from config
  const translatedLabel = useMemo(() => {
    if (label) return label; // Use explicit label if provided
    // First try direct translation key for the sanitized status
    const directKey = `records.processingStatus.${sanitizedStatus}`;
    const directTranslated = t(directKey);
    if (directTranslated !== directKey) return directTranslated;
    // Fallback to config's labelKey
    const configTranslated = t(config.labelKey);
    // If still not found, return the status as-is (capitalized)
    return configTranslated !== config.labelKey
      ? configTranslated
      : sanitizedStatus.charAt(0).toUpperCase() +
          sanitizedStatus.slice(1).replace(/_/g, ' ');
  }, [label, sanitizedStatus, config.labelKey, t]);

  // Unified rendering (handles both spinner and normal cases)
  return renderBadge({
    config,
    label: translatedLabel,
    showIcon,
    size,
    className,
    testId,
    isSpinning: isSpinningStatus,
  });
});
